/* eslint-disable arrow-body-style */
// 下载数据准备：基于 filterInfo/dimensionInfo/metricsInfo 构建全量筛选器，
// 计算有效选中值，提供实时过滤函数（筛选项/指标级联），产出弹窗可展示的 filters/metrics 等
import { useMemo, useCallback } from 'react';
import {
  IFilterInfo,
  IDimensionInfo,
  IMetricInfo,
  IShowFilterItem,
  ICascadeDisplayValue,
} from '../../AIContent/hooks';
import {
  IDownloadConfig,
  TSelectedFilters,
  TSelectedMetrics,
  IFormattedFilter,
  IMetricOption,
  IDownloadData,
  IUseDownloadDataProps,
} from '../types';
import { getDimensionName, getDissionLable } from '../../AIContent/_utils';

/**
 * 合并重复的筛选器选项（按 value 去重，区分大小写）
 */
const mergeDuplicateOptions = (
  options: Array<{ text: string; value: string; icon?: string; icon_active?: string; }>
): Array<{ text: string; value: string; icon: string; icon_active: string; }> => {
  // 使用 Map 按 value 分组（区分大小写）
  const valueMap = new Map<string, {
    text: string;
    value: string;
    icon: string;
    icon_active: string;
  }>();

  options.forEach((option) => {
    // 使用 value 作为 key（区分大小写）
    const valueKey = option.value || '';
    const existing = valueMap.get(valueKey);
    
    if (existing) {
      // 如果已存在相同 value（完全匹配），保留第一个非空的 text、icon、icon_active
      if (!existing.text && option.text) {
        existing.text = option.text;
      }
      if (!existing.icon && option.icon) {
        existing.icon = option.icon;
      }
      if (!existing.icon_active && option.icon_active) {
        existing.icon_active = option.icon_active;
      }
    } else {
      // 保留第一个出现的 value
      valueMap.set(valueKey, {
        text: option.text,
        value: option.value,
        icon: option.icon || '',
        icon_active: option.icon_active || '',
      });
    }
  });

  // 转换为选项数组
  return Array.from(valueMap.values());
};

/**
 * 基于 filterInfo + dimensionInfo 生成全量筛选器（保留级联信息）
 */
const buildFullFilters = (
  filterInfo: IFilterInfo[],
  dimensionInfo: IDimensionInfo[],
  config: IDownloadConfig
): IShowFilterItem[] => {
  const result: IShowFilterItem[] = [];
  const fullDownloadHidden = new Set(config.specialDimensions.fullDownloadHidden);

  filterInfo.forEach((filter) => {
    const dimInfo = dimensionInfo.find((item) => item.data_key === filter.data_key);
    if (!dimInfo) return;

    // 过滤掉特殊维度
    if (fullDownloadHidden.has(filter.data_key)) {
      return;
    }

    if (filter.filter_type === 'normal') {
      const normalFilter: IShowFilterItem = {
        ...dimInfo,
        filter_type: 'normal',
        value: dimInfo.value || [],
      };
      result.push(normalFilter);
    } else if (filter.filter_type === 'cascade') {
      // 级联筛选器：保留 display_value 和 display_info，使用全量 dimensionInfo 的值
      const {
        display_info: displayInfo = [],
        display_value: displayValue = {},
      } = filter;

      // 主级联筛选器
      const cascadeFilter: IShowFilterItem & {
        display_info?: string[];
        display_value?: ICascadeDisplayValue;
      } = {
        ...dimInfo,
        filter_type: 'cascade',
        value: dimInfo.value || [],
        display_info: displayInfo,
        display_value: displayValue,
      };
      result.push(cascadeFilter);

      // 级联子层级（基于 display_info）
      displayInfo.forEach((dimKey, index) => {
        if (index === 0) return; // 第一个是主筛选器，已处理

        const childDimInfo = dimensionInfo.find((item) => item.data_key === dimKey);
        if (!childDimInfo) return;

        // 过滤掉特殊维度
        if (fullDownloadHidden.has(dimKey)) {
          return;
        }

        const cascadeChild: IShowFilterItem & {
          display_info?: string[];
          display_value?: ICascadeDisplayValue;
        } = {
          ...childDimInfo,
          filter_type: 'cascade',
          value: childDimInfo.value || [], // 全量值
          display_info: displayInfo, // 保留级联信息
          display_value: displayValue, // 保留级联信息（用于指标过滤）
        };
        result.push(cascadeChild);
      });
    }
  });

  return result;
};

/**
 * 获取某个级联层的所有父级 data_key（基于 display_info 链路）
 */
const getAllParentKeys = (filter: IShowFilterItem): string[] => {
  const displayInfo = (filter as any)?.display_info as string[] | undefined;
  if (!displayInfo || displayInfo.length === 0) return [];
  const idx = displayInfo.findIndex((key) => key === filter.data_key);
  if (idx <= 0) return [];
  return displayInfo.slice(0, idx);
};

/**
 * 基于所有父级选中值过滤级联子 options
 */
const getFilteredOptions = (
  filter: IShowFilterItem,
  selected: TSelectedFilters,
  allFilters: IShowFilterItem[]
): Array<{ value: string; name?: string; icon?: string; icon_active?: string; } | string> => {
  if (filter.filter_type !== 'cascade') {
    return filter.value || [];
  }
  
  const parentKeys = getAllParentKeys(filter);
  if (parentKeys.length === 0) {
    return filter.value || [];
  }

  // 获取所有父级的选中值
  const parentSelectedValues: string[][] = [];
  let hasAllSelected = true;
  
  parentKeys.forEach((parentKey) => {
    const parentSelected = selected[parentKey] || [];
    if (parentSelected.length === 0) {
      hasAllSelected = false;
    }
    parentSelectedValues.push(parentSelected);
  });

  // 如果任何父级未选，视为全部
  if (!hasAllSelected) {
    return filter.value || [];
  }

  // 获取主级联筛选器的 display_value（第一个筛选器）
  const mainCascadeFilter = allFilters.find((item) => {
    const displayInfo = (item as any)?.display_info as string[] | undefined;
    return displayInfo && displayInfo.length > 0 && item.data_key === displayInfo[0];
  });
  
  const displayValue = (mainCascadeFilter as any)?.display_value as ICascadeDisplayValue | undefined;
  if (!displayValue) {
    return filter.value || [];
  }

  // 递归遍历 display_value，根据所有父级选中值收集子项
  const collect: string[] = [];
  
  const traverse = (
    node: ICascadeDisplayValue | string[] | undefined,
    depth: number
  ): void => {
    if (!node) return;
    
    if (Array.isArray(node)) {
      // 到达叶子节点（指标数组），不应该在这里
      return;
    }
    
    if (depth >= parentKeys.length) {
      // 已经遍历完所有父级，当前节点的键就是子选项
      const keys = Object.keys(node);
      collect.push(...keys);
      return;
    }
    
    const currentParentSelected = parentSelectedValues[depth] || [];
    
    if (currentParentSelected.length === 0) {
      // 当前父级未选，收集所有子节点
      const keys = Object.keys(node);
      keys.forEach((key) => {
        traverse((node as any)[key], depth + 1);
      });
    } else {
      // 根据当前父级选中值继续遍历
      currentParentSelected.forEach((parentVal) => {
        const child = (node as any)?.[parentVal];
        traverse(child, depth + 1);
      });
    }
  };

  traverse(displayValue, 0);

  // 去重
  const unionSet = new Set<string>(collect);

  // 仅保留在原有 value 列表中的选项
  const originValues = (filter.value || []).map((item) => {
    if (!item) return '';
    return typeof item === 'string' ? item : (item?.value || '');
  });
  
  return (filter.value || []).filter((item) => {
    if (!item) return false;
    const val = typeof item === 'string' ? item : (item?.value || '');
    // 如果父级全选，则显示全部选项
    const allParentsSelected = parentSelectedValues.every(ps => ps.length > 0);
    const allParentsFullSelected = parentSelectedValues.every(ps => ps.length === originValues.length);
    return unionSet.has(val) || (allParentsSelected && allParentsFullSelected);
  });
};

/**
 * 计算有效选中值（裁掉无效值 & 单项自动选中）
 */
const computeEffectiveSelected = (
  selectedFilters: TSelectedFilters,
  allFilters: IShowFilterItem[],
  config: IDownloadConfig
): TSelectedFilters => {
  const nextSelected: TSelectedFilters = { ...selectedFilters };

  allFilters.forEach((filter) => {
    const key = filter.data_key;
    const filteredOptions = getFilteredOptions(filter, selectedFilters, allFilters);
    const availableValues = new Set(
      filteredOptions.map((item) => {
        if (!item) return '';
        return typeof item === 'string' ? item : (item?.value || '');
      }),
    );

    const prevVals = nextSelected[key] || [];
    const filteredVals = prevVals.filter((val) => availableValues.has(val));
    nextSelected[key] = filteredVals;

    // 若过滤后仅剩一个可选值且未选中，自动选中
    if (config.cascade.autoSelectSingle && (nextSelected[key]?.length || 0) === 0 && availableValues.size === 1) {
      const [onlyVal] = Array.from(availableValues);
      nextSelected[key] = [onlyVal];
    }
  });

  return nextSelected;
};

/**
 * 根据级联路径过滤指标
 */
const filterMetricsByCascade = (
  metricsInfo: IMetricInfo[],
  allFilters: IShowFilterItem[],
  selectedFilters: TSelectedFilters
): Set<string> => {
  const metricsSet = new Set<string>();

  // 找到主级联筛选器（第一个级联筛选器，包含完整的 display_value）
  const mainCascadeFilter = allFilters.find((filter) => {
    const displayInfo = (filter as any)?.display_info as string[] | undefined;
    return displayInfo && displayInfo.length > 0 && filter.data_key === displayInfo[0];
  });

  if (!mainCascadeFilter) {
    return new Set<string>(); // 没有级联筛选器，返回空集合表示不过滤（显示全部）
  }

  const displayInfo = (mainCascadeFilter as any)?.display_info as string[] | undefined;
  const displayValue = (mainCascadeFilter as any)?.display_value as ICascadeDisplayValue | undefined;
  
  if (!displayInfo || !displayValue || displayInfo.length === 0) {
    return new Set<string>(); // 返回空集合表示不过滤（显示全部）
  }

  // 检查是否所有级联层级（除了最后一个 market_name）都有选中值
  // 因为最后一个层级的子节点是指标数组，所以需要检查到倒数第二层
  let hasAllParentLevelsSelected = true;
  for (let i = 0;i < displayInfo.length - 1;i++) {
    const dimKey = displayInfo[i];
    const selectedVals = selectedFilters[dimKey];
    if (!selectedVals || selectedVals.length === 0) {
      hasAllParentLevelsSelected = false;
      break;
    }
  }

  // 如果父级层级（game_type, source, granularity）未全部选中，返回空集合（不过滤，显示全部）
  if (!hasAllParentLevelsSelected) {
    return new Set<string>(); // 返回空集合表示不过滤（显示全部）
  }

  // 获取最后一个层级（market_name）的选中值
  const lastLevelKey = displayInfo[displayInfo.length - 1];
  const lastLevelSelected = selectedFilters[lastLevelKey] || [];
  
  // 如果最后一个层级未选，返回空集合（不过滤，显示全部）
  if (lastLevelSelected.length === 0) {
    return new Set<string>(); // 返回空集合表示不过滤（显示全部）
  }

  // 递归遍历 display_value，根据所有级联层级选中值收集指标
  const traverse = (
    node: ICascadeDisplayValue | string[] | undefined,
    depth: number,
  ): void => {
    if (!node) return;

    // 深度超出 display_info，不继续
    if (depth > displayInfo.length) {
      return;
    }

    const dimKey = depth < displayInfo.length ? displayInfo[depth] : undefined;

    // 叶子数组应当出现在 depth === displayInfo.length
    if (Array.isArray(node)) {
      if (depth !== displayInfo.length) {
        return;
      }
      // 到达叶子节点，是指标数组
      node.forEach((m) => metricsSet.add(m));
      return;
    }

    if (!dimKey) return;
    
    // 如果是最后一个层级（market_name），使用 lastLevelSelected
    const selectedVals = dimKey === lastLevelKey 
      ? lastLevelSelected 
      : (selectedFilters[dimKey] || []);
    
    if (selectedVals.length === 0) {
      return;
    }

    // 检查是否全选（选中了所有可选值）
    const allKeys = Object.keys(node);
    const isFullSelected = selectedVals.length === allKeys.length;

    // 仅保留在当前节点存在的选中值，避免走不存在的路径
    const matchedVals = selectedVals.filter((val) => val in node);
    if (matchedVals.length === 0) {
      return;
    }

    if (isFullSelected) {
      // 全选：遍历所有子节点
      allKeys.forEach((key) => {
        const child = (node as any)?.[key];
        if (child !== undefined && child !== null) {
          traverse(child as ICascadeDisplayValue | string[] | undefined, depth + 1);
        }
      });
    } else {
      // 部分选中：只遍历选中的子节点，但需要确保选中的值在 node 中存在
      matchedVals.forEach((val) => {
        // 检查选中的值是否在 node 中存在
        if (!(val in node)) {
          return; // 如果不存在，跳过这个值
        }
        const child = (node as any)?.[val];
        // 只有当子节点存在时才继续遍历
        if (child !== undefined && child !== null) {
          traverse(child as ICascadeDisplayValue | string[] | undefined, depth + 1);
        }
      });
    }
  };

  traverse(displayValue, 0);

  // 如果收集到的指标为空，返回空集合表示不过滤（显示全部）
  if (metricsSet.size === 0) {
    // 返回包含哨兵值的 Set，表示“过滤开启但无可用指标”，避免回退为“全部指标”
    return new Set<string>(['__FILTER_EMPTY__']);
  }

  return metricsSet;
};

/**
 * 下载数据准备 Hook
 */
export const useDownloadData = (props: IUseDownloadDataProps): IDownloadData => {
  // 1. 生成全量筛选器（保留级联信息）
  const allFilters = useMemo(() => {
    return buildFullFilters(props.filterInfo, props.dimensionInfo, props.config);
  }, [props.filterInfo, props.dimensionInfo, props.config]);

  // 2. 初始化选中值
  const initialSelectedFilters = useMemo<TSelectedFilters>(() => {
    const result: TSelectedFilters = {};
    allFilters.forEach((filter) => {
      const currentValue = props.currentFilterVal?.[filter.data_key];
      const valueList = filter?.value || [];
      const hasSingleValue = valueList.length === 1;
      let singleValue: string | undefined;
      if (hasSingleValue) {
        const firstVal = valueList[0];
        const isObj = typeof firstVal === 'object' && firstVal !== null;
        singleValue = isObj
          ? (firstVal as any)?.value
          : firstVal as string;
      }

      if (hasSingleValue && singleValue) {
        result[filter.data_key] = [singleValue];
      } else if (currentValue) {
        result[filter.data_key] = [currentValue];
      } else {
        result[filter.data_key] = [];
      }
    });
    return result;
  }, [allFilters, props.currentFilterVal]);

  // 3. 计算有效选中值
  const effectiveSelectedFilters = useMemo<TSelectedFilters>(() => {
    return computeEffectiveSelected(initialSelectedFilters, allFilters, props.config);
  }, [initialSelectedFilters, allFilters, props.config]);

  // 4. 格式化筛选器选项
  const filters = useMemo<IFormattedFilter[]>(() => {
    return allFilters.map((filter) => {
      const key = filter?.data_key || '';
      const rawValueList = getFilteredOptions(filter, effectiveSelectedFilters, allFilters);
      const filterName = filter?.name || getDimensionName(key, props.dimensionInfo) || key;

      const options = rawValueList.map((item) => {
        if (typeof item === 'object' && item !== null) {
          const displayLabel = getDissionLable(key, item.value, item?.name);
          return {
            text: displayLabel || item?.value || '',
            value: item?.value || '',
            icon: item?.icon || '',
            icon_active: item?.icon_active || '',
          };
        }
        const displayLabel = getDissionLable(key, item);
        return {
          text: displayLabel || item || '',
          value: item || '',
          icon: '',
          icon_active: '',
        };
      });

      // 合并重复的选项（按 value 去重）
      const mergedOptions = mergeDuplicateOptions(options);

      return {
        data_key: key,
        name: filterName,
        options: mergedOptions,
      };
    });
  }, [allFilters, effectiveSelectedFilters, props.dimensionInfo]);

  // 5. 处理指标过滤（基于 effectiveSelectedFilters）
  const availableMetricKeys = useMemo<Set<string>>(() => {
    if (!props.config.cascade.enabled) {
      return new Set<string>(); // 未启用级联，返回空集合表示不过滤
    }
    return filterMetricsByCascade(props.metricsInfo, allFilters, effectiveSelectedFilters);
  }, [props.metricsInfo, allFilters, effectiveSelectedFilters, props.config.cascade.enabled]);

  const metrics = useMemo<IMetricOption[]>(() => {
    // availableMetricKeys.size === 0 表示不过滤，显示全部指标
    const enableFilter = availableMetricKeys.size > 0;
    const list = enableFilter
      ? props.metricsInfo.filter((m) => availableMetricKeys.has(m.data_key as string || ''))
      : props.metricsInfo;
    return list.map((metric) => ({
      text: (metric.name as string) || (metric.data_key as string) || '',
      value: (metric.data_key as string) || '',
    }));
  }, [props.metricsInfo, availableMetricKeys]);

  // 6. 计算有效指标选中值
  const initialMetrics = useMemo<TSelectedMetrics>(() => {
    if (metrics.length === 1 && metrics[0]?.value) {
      return [metrics[0].value];
    }
    return props.currentMetric ? [props.currentMetric] : [];
  }, [metrics, props.currentMetric]);

  const effectiveSelectedMetrics = useMemo<TSelectedMetrics>(() => {
    const enableFilter = availableMetricKeys.size > 0;
    const next = enableFilter
      ? initialMetrics.filter((m) => availableMetricKeys.has(m))
      : [...initialMetrics];
    if (next.length === 0 && metrics.length === 1) {
      return [metrics[0].value];
    }
    return next;
  }, [initialMetrics, availableMetricKeys, metrics]);

  // 创建实时计算函数
  const getFilteredOptionsRealTime = useCallback((
    filter: IShowFilterItem,
    selected: TSelectedFilters
  ): Array<{ value: string; name?: string; icon?: string; icon_active?: string; } | string> => {
    return getFilteredOptions(filter, selected, allFilters);
  }, [allFilters]);

  const filterMetricsByCascadeRealTime = useCallback((selectedFilters: TSelectedFilters): Set<string> => {
    if (!props.config.cascade.enabled) {
      return new Set<string>(); // 未启用级联，返回空集合表示不过滤
    }
    return filterMetricsByCascade(props.metricsInfo, allFilters, selectedFilters);
  }, [props.metricsInfo, allFilters, props.config.cascade.enabled]);

  const getDimensionNameRealTime = useCallback((key: string): string => {
    return getDimensionName(key, props.dimensionInfo) || key;
  }, [props.dimensionInfo]);

  return {
    filters,
    metrics,
    allFilters, // 导出全量筛选器，用于实时计算
    effectiveSelected: {
      filters: effectiveSelectedFilters,
      metrics: effectiveSelectedMetrics,
    },
    getFilteredOptions: getFilteredOptionsRealTime,
    filterMetricsByCascade: filterMetricsByCascadeRealTime,
    getDimensionName: getDimensionNameRealTime,
  };
};

